import { createContext, forwardRef, useContext, type MouseEvent, type ReactElement, type ReactNode, type Ref } from 'react'
import { Menu as BaseMenu } from '@base-ui/react/menu'
import { Link, type To } from 'react-router-dom'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import './menu.css'

type MenuRootProps = Pick<BaseMenu.Root.Props, 'open' | 'defaultOpen' | 'onOpenChange' | 'onOpenChangeComplete' | 'modal' | 'disabled'>

/** Where a MenuLinkItem leads: `to` for app routes, `href` for anything else. */
export type MenuLinkTarget = { to: To; href?: never } | { href: string; to?: never }

/** Called on every click of a link item, with its target and, for app routes, whether it replaces the current entry. */
type OnNavigate = (event: MouseEvent<HTMLAnchorElement>, link: MenuLinkTarget & { replace?: boolean }) => void

/** The menu's `onNavigate`, for the link items inside it. */
const NavigateContext = createContext<OnNavigate | undefined>(undefined)

export type MenuProps = MenuRootProps & Omit<BaseMenu.Popup.Props, 'children' | 'className' | 'render'> & {
  /** Required. The named button that opens the menu: an IconButton or a Button. */
  trigger: ReactElement
  /** MenuItem, MenuLinkItem, MenuRadioGroup, MenuGroup and MenuSeparator. */
  children: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
  /** Called on every click of a MenuLinkItem inside, after its own `onClick`, with the click and the item's `to` (and
   *  `replace`) or `href`: for example to hold leaving. It fires for modifier and middle clicks too, so callers filter
   *  those. `event.preventDefault()` stays on the page; the menu closes either way. */
  onNavigate?: OnNavigate
  /** Classes for the menu surface. */
  className?: string
}

/** A short list of actions, destinations or one choice, opened from a button. Arrow keys move, typing jumps, Escape closes. */
export const Menu = forwardRef<HTMLDivElement, MenuProps>(function Menu(
  { open, defaultOpen, onOpenChange, onOpenChangeComplete, modal, disabled, trigger, children, side = 'bottom', align = 'start', container, onNavigate, className, ...popup },
  ref,
) {
  return (
    <BaseMenu.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} onOpenChangeComplete={onOpenChangeComplete} modal={modal} disabled={disabled}>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal container={container ?? undefined}>
        <BaseMenu.Positioner side={side} align={align} sideOffset={6} collisionPadding={12} className="play-menu__positioner">
          <BaseMenu.Popup ref={ref} className={cn('play-theme play-menu', className)} {...popup}>
            <NavigateContext.Provider value={onNavigate}>{children}</NavigateContext.Provider>
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  )
})

function ItemContent({ icon, children }: { icon?: ReactElement; children: ReactNode }) {
  return (
    <>
      {icon && <span className="play-menu__icon" aria-hidden="true">{icon}</span>}
      <span className="play-menu__label">{children}</span>
    </>
  )
}

export type MenuItemProps = Omit<BaseMenu.Item.Props, 'className' | 'render'> & {
  /** A Lucide icon before the label. Decorative: the label names the item. */
  icon?: ReactElement
  className?: string
}

/** An action. The menu closes after it runs. */
export const MenuItem = forwardRef<HTMLElement, MenuItemProps>(function MenuItem({ icon, className, children, ...props }, ref) {
  return (
    <BaseMenu.Item ref={ref} className={cn('play-menu__item', className)} {...props}>
      <ItemContent icon={icon}>{children}</ItemContent>
    </BaseMenu.Item>
  )
})

export type MenuLinkItemProps = MenuLinkTarget & Omit<BaseMenu.LinkItem.Props, 'className' | 'render' | 'href'> & {
  /** A Lucide icon before the label. Decorative: the label names the item. */
  icon?: ReactElement
  /** Replace the current history entry instead of adding one. Router links only. */
  replace?: boolean
  className?: string
}

/** Navigation. Renders a real link: `to` for app routes through React Router, `href` for anything else. */
export const MenuLinkItem = forwardRef<HTMLAnchorElement, MenuLinkItemProps>(function MenuLinkItem(
  { to, href, replace, icon, closeOnClick = true, className, children, onClick, ...props },
  ref,
) {
  const onNavigate = useContext(NavigateContext)
  const target = to !== undefined ? { render: <Link to={to} replace={replace} /> } : { href }
  const handleClick: MenuLinkItemProps['onClick'] = (event) => {
    onClick?.(event)
    onNavigate?.(event, to !== undefined ? { to, replace } : { href: href as string })
  }
  return (
    <BaseMenu.LinkItem ref={ref as Ref<Element>} closeOnClick={closeOnClick} className={cn('play-menu__item', className)} onClick={handleClick} {...target} {...props}>
      <ItemContent icon={icon}>{children}</ItemContent>
    </BaseMenu.LinkItem>
  )
})

export type MenuRadioOption<Value extends string> = { value: Value; label: string; disabled?: boolean }

export type MenuRadioGroupProps<Value extends string> = {
  /** Required. Names the choice, such as “Sort by”. Shown as the group label unless `hideLabel`. */
  label: string
  /** Keeps the label for assistive technology only, when the trigger already says it. */
  hideLabel?: boolean
  items: readonly MenuRadioOption<Value>[]
  value?: Value
  defaultValue?: Value
  onValueChange?: (value: Value) => void
  /** Close the menu once something is chosen. */
  closeOnClick?: boolean
  disabled?: boolean
}

/** One choice from a short list, such as a sort order or the active organization. The chosen option shows a check. */
export function MenuRadioGroup<Value extends string>({
  label, hideLabel = false, items, value, defaultValue, onValueChange, closeOnClick = true, disabled,
}: MenuRadioGroupProps<Value>) {
  return (
    <BaseMenu.RadioGroup
      value={value}
      defaultValue={defaultValue}
      onValueChange={(next: Value) => onValueChange?.(next)}
      disabled={disabled}
      aria-label={hideLabel ? label : undefined}
      className="play-menu__group"
    >
      {!hideLabel && <BaseMenu.GroupLabel className="play-menu__group-label">{label}</BaseMenu.GroupLabel>}
      {items.map((item) => (
        <BaseMenu.RadioItem key={item.value} value={item.value} label={item.label} disabled={item.disabled} closeOnClick={closeOnClick} className="play-menu__item">
          <span className="play-menu__label">{item.label}</span>
          <BaseMenu.RadioItemIndicator className="play-menu__indicator"><Check aria-hidden="true" /></BaseMenu.RadioItemIndicator>
        </BaseMenu.RadioItem>
      ))}
    </BaseMenu.RadioGroup>
  )
}

/** Related items under a short visible label. */
export function MenuGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <BaseMenu.Group className="play-menu__group">
      <BaseMenu.GroupLabel className="play-menu__group-label">{label}</BaseMenu.GroupLabel>
      {children}
    </BaseMenu.Group>
  )
}

/** A divider between groups of items. */
export function MenuSeparator() {
  return <BaseMenu.Separator className="play-menu__separator" />
}
